import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Statistic,
  Spin, message, Table, Modal, Form, Input, InputNumber,
  DatePicker, Select, Tabs, Alert, Popconfirm, Progress,
  Empty, Badge, Tooltip, Divider, Upload, Image
} from 'antd';
import {
  FileTextOutlined,
  PlusOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckCircleFilled,
  SyncOutlined,
  UploadOutlined,
  EyeOutlined,
  EditOutlined,
  CompassOutlined,
  CarOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
  FileDoneOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import StaffLayout from './StaffLayout';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const fmtCurrency = (val) =>
  `₹${(Number(val) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EXPENSE_CATEGORIES = [
  'Travel',
  'Food',
  'Fuel',
  'Accommodation',
  'Office Supplies',
  'Communication',
  'Client Entertainment',
  'Medical',
  'Other'
];

const TRANSPORT_MODES = [
  'Car',
  'Bike',
  'Bus',
  'Train',
  'Flight',
  'Auto / Cab',
  'Metro',
  'Other'
];

const StaffExpense = () => {
  const [loading, setLoading] = useState(false);
  const [claims, setClaims] = useState([]);
  const [activeTab, setActiveTab] = useState('ALL');

  // Submit / Edit Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingClaim, setEditingClaim] = useState(null);
  const [expenseDate, setExpenseDate] = useState(dayjs());
  const [globalDesc, setGlobalDesc] = useState('');

  // Dynamic expense items inside the modal
  const [items, setItems] = useState([
    {
      expenseType: 'Travel',
      amount: '',
      billNumber: '',
      description: '',
      travelFrom: '',
      travelTo: '',
      mode: 'Car',
      file: null,
      attachmentUrl: null
    }
  ]);

  // Details Modal state
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);

  // Fetch expense claims from backend
  const fetchClaims = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/me/expenses');
      if (res.data?.success) {
        setClaims(res.data.claims || []);
      }
    } catch (err) {
      console.error('Error fetching expense claims:', err);
      message.error(err.response?.data?.message || 'Failed to load expense claims');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  // Summary Metrics Computations
  const summary = useMemo(() => {
    let totalClaimed = 0;
    let totalApproved = 0;
    let totalPaid = 0;
    let pendingCount = 0;
    let approvedCount = 0;
    let rejectedCount = 0;

    claims.forEach((c) => {
      const amt = Number(c.amount || 0);
      const appAmt = Number(c.approvedAmount || amt);
      const pdAmt = Number(c.paidAmount || 0);

      totalClaimed += amt;
      if (c.status === 'approved') {
        totalApproved += appAmt;
        totalPaid += pdAmt;
        approvedCount++;
      } else if (c.status === 'pending') {
        pendingCount++;
      } else if (c.status === 'rejected') {
        rejectedCount++;
      }
    });

    return {
      totalClaimed,
      totalApproved,
      totalPaid,
      pendingCount,
      approvedCount,
      rejectedCount,
      totalCount: claims.length
    };
  }, [claims]);

  // Filtered Claims based on Tab
  const filteredClaims = useMemo(() => {
    if (activeTab === 'ALL') return claims;
    return claims.filter((c) => (c.status || 'pending').toLowerCase() === activeTab.toLowerCase());
  }, [claims, activeTab]);

  // Open Submit Modal (Clean State)
  const handleOpenAddModal = () => {
    setEditingClaim(null);
    setExpenseDate(dayjs());
    setGlobalDesc('');
    setItems([
      {
        expenseType: 'Travel',
        amount: '',
        billNumber: '',
        description: '',
        travelFrom: '',
        travelTo: '',
        mode: 'Car',
        file: null,
        attachmentUrl: null
      }
    ]);
    setModalVisible(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (claim) => {
    if (claim.status !== 'pending') {
      message.warning('Only pending claims can be edited');
      return;
    }
    setEditingClaim(claim);
    setExpenseDate(claim.expenseDate ? dayjs(claim.expenseDate) : dayjs());
    setGlobalDesc(claim.description || '');

    let parsedItems = [];
    if (claim.items) {
      try {
        parsedItems = typeof claim.items === 'string' ? JSON.parse(claim.items) : claim.items;
      } catch (_) {
        parsedItems = [];
      }
    }

    if (!Array.isArray(parsedItems) || parsedItems.length === 0) {
      parsedItems = [
        {
          expenseType: claim.expenseType || 'Travel',
          amount: claim.amount || '',
          billNumber: claim.billNumber || '',
          description: claim.description || '',
          travelFrom: claim.travelFrom || '',
          travelTo: claim.travelTo || '',
          mode: claim.mode || 'Car',
          attachmentUrl: claim.attachmentUrl || null,
          file: null
        }
      ];
    } else {
      parsedItems = parsedItems.map((it) => ({
        expenseType: it.expenseType || 'Travel',
        amount: it.amount || '',
        billNumber: it.billNumber || '',
        description: it.description || '',
        travelFrom: it.travelFrom || '',
        travelTo: it.travelTo || '',
        mode: it.mode || 'Car',
        attachmentUrl: it.attachmentUrl || null,
        file: null
      }));
    }

    setItems(parsedItems);
    setModalVisible(true);
  };

  // Modify individual item row
  const updateItemField = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  // Add Item Row
  const handleAddItemRow = () => {
    setItems((prev) => [
      ...prev,
      {
        expenseType: 'Food',
        amount: '',
        billNumber: '',
        description: '',
        travelFrom: '',
        travelTo: '',
        mode: 'Car',
        file: null,
        attachmentUrl: null
      }
    ]);
  };

  // Remove Item Row
  const handleRemoveItemRow = (index) => {
    if (items.length === 1) {
      message.warning('At least one expense item is required');
      return;
    }
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Total Claim Amount inside modal
  const modalTotalAmount = useMemo(() => {
    return items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
  }, [items]);

  // Submit Claim (FormData with multipart files)
  const handleSubmitClaim = async () => {
    // Validation
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.amount || Number(it.amount) <= 0) {
        message.error(`Please enter a valid amount for Item #${i + 1}`);
        return;
      }
      if (it.expenseType === 'Travel') {
        if (!it.travelFrom?.trim()) {
          message.error(`Please enter "From Location" for Item #${i + 1}`);
          return;
        }
        if (!it.travelTo?.trim()) {
          message.error(`Please enter "To Location" for Item #${i + 1}`);
          return;
        }
      }
    }

    try {
      setSubmitting(true);
      const form = new FormData();
      form.append('expenseType', items[0]?.expenseType || 'Other');
      form.append('expenseDate', expenseDate.format('YYYY-MM-DD'));
      form.append('description', globalDesc || '');

      // Prepare items for serialization
      const serialized = items.map((it) => ({
        expenseType: it.expenseType,
        amount: Number(it.amount),
        billNumber: it.billNumber || null,
        description: it.description || null,
        travelFrom: it.travelFrom || null,
        travelTo: it.travelTo || null,
        mode: it.mode || null,
        attachmentUrl: it.attachmentUrl || null
      }));

      form.append('items', JSON.stringify(serialized));

      // Append files with fieldname attachment_0, attachment_1 etc.
      items.forEach((it, idx) => {
        if (it.file) {
          form.append(`attachment_${idx}`, it.file);
        }
      });

      if (editingClaim?.id) {
        await api.put(`/me/expenses/${editingClaim.id}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Expense claim updated successfully');
      } else {
        await api.post('/me/expenses', form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        message.success('Expense claim submitted successfully for approval');
      }

      setModalVisible(false);
      fetchClaims();
    } catch (err) {
      console.error('Error submitting claim:', err);
      message.error(err.response?.data?.message || 'Failed to submit expense claim');
    } finally {
      setSubmitting(false);
    }
  };

  // Table Columns for Expense Claims
  const columns = [
    {
      title: 'Claim ID & Date',
      key: 'id',
      width: 170,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13 }}>
            {r.claimId || `#EC-${r.id}`}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
            <CalendarOutlined style={{ marginRight: 4 }} />
            {dayjs(r.expenseDate).format('DD MMM YYYY')}
          </div>
        </div>
      )
    },
    {
      title: 'Category & Details',
      key: 'category',
      width: 220,
      render: (_, r) => {
        const isTravel = (r.expenseType || '').toLowerCase() === 'travel';
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Tag color={isTravel ? 'blue' : 'purple'} style={{ borderRadius: 6, fontWeight: 600 }}>
                {r.expenseType || 'General Expense'}
              </Tag>
              {r.billNumber && (
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  Bill: {r.billNumber}
                </span>
              )}
            </div>

            {isTravel && (r.travelFrom || r.travelTo) && (
              <div style={{ fontSize: 12, color: '#334155', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                <CarOutlined style={{ color: '#2563eb' }} />
                <span>{r.travelFrom || 'Origin'} ➔ {r.travelTo || 'Dest'} ({r.mode || 'Car'})</span>
              </div>
            )}

            {r.description && (
              <div style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                "{r.description}"
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Claimed Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      render: (amt) => (
        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>
          {fmtCurrency(amt)}
        </span>
      )
    },
    {
      title: 'Status & Settlement',
      key: 'status',
      width: 210,
      render: (_, r) => {
        const st = (r.status || 'pending').toLowerCase();
        if (st === 'approved') {
          const appAmt = Number(r.approvedAmount || r.amount);
          const pdAmt = Number(r.paidAmount || 0);
          return (
            <div>
              <Tag color="success" icon={<CheckCircleFilled />} style={{ borderRadius: 6, fontWeight: 700 }}>
                Approved: {fmtCurrency(appAmt)}
              </Tag>
              <div style={{ marginTop: 4 }}>
                {pdAmt >= appAmt ? (
                  <Tag color="green" style={{ borderRadius: 4, fontSize: 11 }}>
                    Fully Reimbursed ({fmtCurrency(pdAmt)})
                  </Tag>
                ) : pdAmt > 0 ? (
                  <Tag color="cyan" style={{ borderRadius: 4, fontSize: 11 }}>
                    Partial Paid: {fmtCurrency(pdAmt)}
                  </Tag>
                ) : (
                  <Tag color="blue" style={{ borderRadius: 4, fontSize: 11 }}>
                    Pending Disbursement
                  </Tag>
                )}
              </div>
            </div>
          );
        }

        if (st === 'rejected') {
          return (
            <Tag color="error" icon={<CloseCircleOutlined />} style={{ borderRadius: 6, fontWeight: 700 }}>
              Rejected
            </Tag>
          );
        }

        return (
          <Tag color="warning" icon={<ClockCircleOutlined />} style={{ borderRadius: 6, fontWeight: 700 }}>
            Pending Approval
          </Tag>
        );
      }
    },
    {
      title: 'Receipt',
      key: 'receipt',
      width: 110,
      render: (_, r) => {
        if (!r.attachmentUrl) return <Text type="secondary" style={{ fontSize: 12 }}>No bill</Text>;
        const baseURL = api.defaults.baseURL || 'http://localhost:4000';
        const fullUrl = r.attachmentUrl.startsWith('http') ? r.attachmentUrl : `${baseURL}${r.attachmentUrl}`;
        const isPdf = r.attachmentUrl.toLowerCase().endsWith('.pdf');

        return isPdf ? (
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => window.open(fullUrl, '_blank')}
            style={{ borderRadius: 6, fontSize: 11 }}
          >
            PDF
          </Button>
        ) : (
          <Image
            src={fullUrl}
            alt="Receipt"
            width={38}
            height={38}
            style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0', cursor: 'pointer' }}
          />
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      render: (_, r) => (
        <Space size="small">
          <Button
            size="small"
            onClick={() => {
              setSelectedClaim(r);
              setDetailsModalVisible(true);
            }}
            style={{ borderRadius: 6, fontSize: 12 }}
          >
            Details
          </Button>

          {r.status === 'pending' && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEditModal(r)}
              style={{ borderRadius: 6, fontSize: 12 }}
            >
              Edit
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <StaffLayout>
      <div style={{ paddingBottom: 40 }}>
        {/* 1. HEADER CARD */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            marginBottom: 20,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <Space align="center" size={14}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#2563eb',
                fontSize: 24
              }}>
                <FileTextOutlined />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
                  Expense Claims & Reimbursements
                </Title>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Submit business expenses, upload bills, track approval & reimbursement status
                </Text>
              </div>
            </Space>

            <Space wrap size="middle">
              <Button
                icon={<SyncOutlined />}
                onClick={fetchClaims}
                loading={loading}
                style={{ borderRadius: 8, height: 38 }}
              >
                Refresh
              </Button>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenAddModal}
                style={{
                  borderRadius: 8,
                  height: 38,
                  background: '#2563eb',
                  borderColor: '#2563eb',
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(37, 99, 235, 0.15)'
                }}
              >
                Submit Expense Claim
              </Button>
            </Space>
          </div>
        </Card>

        {/* 2. TOP METRIC SUMMARY CARDS */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginBottom: 20
        }}>
          {/* Total Claimed */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #bfdbfe',
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#1d4ed8', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Total Claimed
              </Text>
              <span style={{ fontSize: 20 }}>📋</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1e3a8a', lineHeight: 1.2 }}>
              {fmtCurrency(summary.totalClaimed)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#1e40af', fontWeight: 500 }}>
              Across {summary.totalCount} submitted claim(s)
            </div>
          </Card>

          {/* Approved Amount */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #bbf7d0',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#15803d', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Approved Claims
              </Text>
              <span style={{ fontSize: 20 }}>✅</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#14532d', lineHeight: 1.2 }}>
              {fmtCurrency(summary.totalApproved)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#166534', fontWeight: 500 }}>
              {summary.approvedCount} claim(s) approved by HR/Admin
            </div>
          </Card>

          {/* Reimbursed / Paid */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #ddd6fe',
              background: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#6d28d9', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Reimbursed Amount
              </Text>
              <span style={{ fontSize: 20 }}>💳</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#4c1d95', lineHeight: 1.2 }}>
              {fmtCurrency(summary.totalPaid)}
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#5b21b6', fontWeight: 500 }}>
              Settled to your bank account
            </div>
          </Card>

          {/* Pending Review */}
          <Card
            style={{
              borderRadius: 16,
              border: '1px solid #fde68a',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '20px 22px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ color: '#b45309', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Pending Review
              </Text>
              <span style={{ fontSize: 20 }}>⏳</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#78350f', lineHeight: 1.2 }}>
              {summary.pendingCount} Claim(s)
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', fontWeight: 500 }}>
              Awaiting manager / admin review
            </div>
          </Card>
        </div>

        {/* 3. CLAIMS TABLE CARD */}
        <Card
          style={{
            borderRadius: 16,
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}
          bodyStyle={{ padding: 24 }}
        >
          {/* Tabs Filter */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <Space wrap>
              {[
                { key: 'ALL', label: `All Claims (${summary.totalCount})` },
                { key: 'pending', label: `Pending (${summary.pendingCount})` },
                { key: 'approved', label: `Approved (${summary.approvedCount})` },
                { key: 'rejected', label: `Rejected (${summary.rejectedCount})` }
              ].map(t => (
                <Button
                  key={t.key}
                  type={activeTab === t.key ? 'primary' : 'default'}
                  onClick={() => setActiveTab(t.key)}
                  style={{ borderRadius: 8, fontWeight: 500 }}
                >
                  {t.label}
                </Button>
              ))}
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={filteredClaims}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: 'No expense claims found' }}
            scroll={{ x: 1000 }}
            style={{ borderRadius: 8 }}
          />
        </Card>

        {/* 4. SUBMIT / EDIT EXPENSE MODAL */}
        <Modal
          title={
            <Space>
              <FileTextOutlined style={{ color: '#2563eb', fontSize: 18 }} />
              <span style={{ fontWeight: 700 }}>
                {editingClaim ? 'Edit Expense Claim' : 'Submit New Expense Claim'}
              </span>
            </Space>
          }
          open={modalVisible}
          onCancel={() => {
            if (!submitting) setModalVisible(false);
          }}
          onOk={handleSubmitClaim}
          confirmLoading={submitting}
          okText={editingClaim ? 'Save Changes' : 'Submit Claim'}
          okButtonProps={{
            style: {
              background: '#2563eb',
              borderColor: '#2563eb',
              borderRadius: 6,
              fontWeight: 600
            }
          }}
          cancelButtonProps={{ borderRadius: 6 }}
          width={720}
        >
          <div style={{ marginTop: 12 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#334155' }}>
                    Expense Date *
                  </label>
                  <DatePicker
                    value={expenseDate}
                    onChange={(d) => d && setExpenseDate(d)}
                    style={{ width: '100%', borderRadius: 6 }}
                    format="DD MMM YYYY"
                    allowClear={false}
                  />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#334155' }}>
                    Claim Summary / Notes (Optional)
                  </label>
                  <Input
                    placeholder="e.g. Client visit to Ranchi"
                    value={globalDesc}
                    onChange={(e) => setGlobalDesc(e.target.value)}
                    style={{ borderRadius: 6 }}
                  />
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: '12px 0 16px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Text strong style={{ fontSize: 14, color: '#0f172a' }}>
                Itemized Expenses ({items.length})
              </Text>
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddItemRow}
                style={{ borderRadius: 6, fontWeight: 500 }}
              >
                Add Another Item
              </Button>
            </div>

            {/* Dynamic Items List */}
            <div style={{ maxHeight: 380, overflowY: 'auto', paddingRight: 4 }}>
              {items.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 14,
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <Tag color="blue" style={{ borderRadius: 6, fontWeight: 700 }}>
                      Item #{idx + 1}
                    </Tag>
                    {items.length > 1 && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveItemRow(idx)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <Row gutter={12}>
                    <Col xs={24} sm={12}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                          Category *
                        </label>
                        <Select
                          value={item.expenseType}
                          onChange={(val) => updateItemField(idx, 'expenseType', val)}
                          style={{ width: '100%', borderRadius: 6 }}
                        >
                          {EXPENSE_CATEGORIES.map((cat) => (
                            <Option key={cat} value={cat}>{cat}</Option>
                          ))}
                        </Select>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                          Amount (₹) *
                        </label>
                        <InputNumber
                          value={item.amount}
                          onChange={(val) => updateItemField(idx, 'amount', val)}
                          style={{ width: '100%', borderRadius: 6 }}
                          formatter={(v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(v) => v.replace(/₹\s?|(,*)/g, '')}
                          placeholder="0.00"
                          min={1}
                        />
                      </div>
                    </Col>

                    {/* Travel specific fields */}
                    {item.expenseType === 'Travel' && (
                      <>
                        <Col xs={24} sm={8}>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                              From Location *
                            </label>
                            <Input
                              placeholder="Origin"
                              value={item.travelFrom}
                              onChange={(e) => updateItemField(idx, 'travelFrom', e.target.value)}
                              style={{ borderRadius: 6 }}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={8}>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                              To Location *
                            </label>
                            <Input
                              placeholder="Destination"
                              value={item.travelTo}
                              onChange={(e) => updateItemField(idx, 'travelTo', e.target.value)}
                              style={{ borderRadius: 6 }}
                            />
                          </div>
                        </Col>
                        <Col xs={24} sm={8}>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                              Mode of Transport
                            </label>
                            <Select
                              value={item.mode}
                              onChange={(val) => updateItemField(idx, 'mode', val)}
                              style={{ width: '100%', borderRadius: 6 }}
                            >
                              {TRANSPORT_MODES.map((m) => (
                                <Option key={m} value={m}>{m}</Option>
                              ))}
                            </Select>
                          </div>
                        </Col>
                      </>
                    )}

                    <Col xs={24} sm={12}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                          Bill / Invoice Number
                        </label>
                        <Input
                          placeholder="e.g. INV-8821 (optional)"
                          value={item.billNumber}
                          onChange={(e) => updateItemField(idx, 'billNumber', e.target.value)}
                          style={{ borderRadius: 6 }}
                        />
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                          Upload Receipt / Bill (JPG, PNG, PDF)
                        </label>
                        <Upload
                          beforeUpload={(file) => {
                            updateItemField(idx, 'file', file);
                            return false; // Prevent automatic upload
                          }}
                          maxCount={1}
                          showUploadList={true}
                        >
                          <Button size="small" icon={<UploadOutlined />} style={{ borderRadius: 6 }}>
                            {item.file ? item.file.name : 'Choose File'}
                          </Button>
                        </Upload>
                      </div>
                    </Col>

                    <Col span={24}>
                      <div>
                        <Input
                          placeholder="Item description / remarks (optional)"
                          value={item.description}
                          onChange={(e) => updateItemField(idx, 'description', e.target.value)}
                          style={{ borderRadius: 6 }}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
            </div>

            {/* Total Calculation Strip */}
            <div style={{
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              padding: '12px 18px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 12
            }}>
              <div>
                <strong style={{ color: '#0f172a' }}>Total Claim Amount:</strong>
                <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>
                  ({items.length} items)
                </span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#2563eb' }}>
                {fmtCurrency(modalTotalAmount)}
              </div>
            </div>
          </div>
        </Modal>

        {/* 5. VIEW CLAIM DETAILS MODAL */}
        <Modal
          title={
            <Space>
              <FileDoneOutlined style={{ color: '#2563eb' }} />
              <span style={{ fontWeight: 700 }}>Expense Claim #{selectedClaim?.claimId || selectedClaim?.id}</span>
            </Space>
          }
          open={detailsModalVisible}
          onCancel={() => setDetailsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDetailsModalVisible(false)} style={{ borderRadius: 6 }}>
              Close
            </Button>
          ]}
          width={640}
        >
          {selectedClaim && (
            <div style={{ marginTop: 12 }}>
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '16px',
                marginBottom: 16
              }}>
                <Row gutter={[16, 12]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Expense Date</Text>
                    <div><strong>{dayjs(selectedClaim.expenseDate).format('DD MMM YYYY')}</strong></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Status</Text>
                    <div>
                      {selectedClaim.status === 'approved' ? (
                        <Tag color="success">Approved</Tag>
                      ) : selectedClaim.status === 'rejected' ? (
                        <Tag color="error">Rejected</Tag>
                      ) : (
                        <Tag color="warning">Pending Approval</Tag>
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Claimed Amount</Text>
                    <div><strong style={{ color: '#0f172a', fontSize: 16 }}>{fmtCurrency(selectedClaim.amount)}</strong></div>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Approved Amount</Text>
                    <div>
                      <strong style={{ color: '#16a34a', fontSize: 16 }}>
                        {selectedClaim.status === 'approved' ? fmtCurrency(selectedClaim.approvedAmount || selectedClaim.amount) : '--'}
                      </strong>
                    </div>
                  </Col>
                  {Number(selectedClaim.paidAmount) > 0 && (
                    <Col span={12}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Reimbursed / Paid</Text>
                      <div><strong style={{ color: '#7c3aed', fontSize: 15 }}>{fmtCurrency(selectedClaim.paidAmount)}</strong></div>
                    </Col>
                  )}
                  {selectedClaim.description && (
                    <Col span={24}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Notes / Summary</Text>
                      <div style={{ fontStyle: 'italic', color: '#334155' }}>
                        "{selectedClaim.description}"
                      </div>
                    </Col>
                  )}
                </Row>
              </div>

              {/* Items Breakdown */}
              <Title level={5} style={{ margin: '0 0 12px 0' }}>Itemized Expenses</Title>
              {(() => {
                let parsed = [];
                try {
                  parsed = typeof selectedClaim.items === 'string' ? JSON.parse(selectedClaim.items) : selectedClaim.items;
                } catch (_) {}

                if (!Array.isArray(parsed) || parsed.length === 0) {
                  parsed = [{
                    expenseType: selectedClaim.expenseType,
                    amount: selectedClaim.amount,
                    billNumber: selectedClaim.billNumber,
                    travelFrom: selectedClaim.travelFrom,
                    travelTo: selectedClaim.travelTo,
                    mode: selectedClaim.mode,
                    description: selectedClaim.description
                  }];
                }

                return (
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={parsed.map((p, i) => ({ ...p, key: i }))}
                    columns={[
                      {
                        title: 'Item & Details',
                        key: 'desc',
                        render: (_, it) => (
                          <div>
                            <Tag color="blue" style={{ borderRadius: 4, fontWeight: 600 }}>{it.expenseType || 'Expense'}</Tag>
                            {it.travelFrom && it.travelTo && (
                              <div style={{ fontSize: 11, color: '#334155', marginTop: 2 }}>
                                {it.travelFrom} ➔ {it.travelTo} ({it.mode || 'Car'})
                              </div>
                            )}
                            {it.billNumber && (
                              <div style={{ fontSize: 11, color: '#64748b' }}>Bill: {it.billNumber}</div>
                            )}
                          </div>
                        )
                      },
                      {
                        title: 'Amount',
                        dataIndex: 'amount',
                        key: 'amount',
                        render: (amt) => <strong>{fmtCurrency(amt)}</strong>
                      }
                    ]}
                    style={{ borderRadius: 8, marginBottom: 16 }}
                  />
                );
              })()}

              {/* Receipt Preview */}
              {selectedClaim.attachmentUrl && (
                <div>
                  <Title level={5} style={{ margin: '0 0 8px 0' }}>Attached Bill / Receipt</Title>
                  {(() => {
                    const baseURL = api.defaults.baseURL || 'http://localhost:4000';
                    const fullUrl = selectedClaim.attachmentUrl.startsWith('http')
                      ? selectedClaim.attachmentUrl
                      : `${baseURL}${selectedClaim.attachmentUrl}`;
                    const isPdf = selectedClaim.attachmentUrl.toLowerCase().endsWith('.pdf');

                    return isPdf ? (
                      <Button
                        type="primary"
                        icon={<FileTextOutlined />}
                        onClick={() => window.open(fullUrl, '_blank')}
                        style={{ borderRadius: 6 }}
                      >
                        Open Bill Document (PDF)
                      </Button>
                    ) : (
                      <Image
                        src={fullUrl}
                        alt="Bill Receipt"
                        style={{ maxWidth: 300, maxHeight: 300, borderRadius: 8, border: '1px solid #cbd5e1' }}
                      />
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </StaffLayout>
  );
};

export default StaffExpense;
